import { invalidateNutritionQueries } from "@/lib/nutritionQueries";
import { SecureImage } from "@/components/ui/SecureImage";
import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Camera,
  Check,
  Lightbulb,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
  Scale,
} from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { BackButton } from "@/components/ui/BackButton";
import {
  addMealItem,
  addMealPhoto,
  deleteMealItem,
  deleteMealPhoto,
  getMeal,
  reanalyzeMeal,
  updateMealItem,
  updateMealMetadata,
} from "@/api/nutrition";
import type {
  MealAlternative,
  MealEntry,
  MealItem,
  MealItemCreate,
  MealItemUpdate,
  MealPhoto,
} from "@/types";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/dates";

const MEAL_TYPE_LABEL: Record<string, string> = {
  breakfast: "meal_type.breakfast",
  lunch: "meal_type.lunch",
  dinner: "meal_type.dinner",
  snack: "meal_type.snack",
  pre_workout: "meal_type.pre_workout",
  post_workout: "meal_type.post_workout",
};

function n(v: number, d = 0): string {
  return v.toLocaleString("ru-RU", { maximumFractionDigits: d });
}

export default function MealDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const mealId = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();

  const mealQ = useQuery({
    queryKey: ["meal", mealId],
    queryFn: () => getMeal(mealId),
    enabled: Number.isFinite(mealId) && mealId > 0,
  });

  const invalidateLists = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["nutrition-meals"] }),
      invalidateNutritionQueries(qc),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);

  const applyResponse = (res: { meal: MealEntry }) => {
    qc.setQueryData(["meal", mealId], res.meal);
    void invalidateLists();
  };

  const [reanalyzeOpen, setReanalyzeOpen] = useState(false);
  const [reanalyzeHint, setReanalyzeHint] = useState("");

  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");

  const updateM = useMutation({
    mutationFn: (args: { itemId: number; patch: MealItemUpdate }) =>
      updateMealItem(mealId, args.itemId, args.patch),
    onSuccess: applyResponse,
  });

  const updateMetaM = useMutation({
    mutationFn: (payload: import("@/types").MealMetadataUpdate) =>
      updateMealMetadata(mealId, payload),
    onSuccess: (meal) => {
      qc.setQueryData(["meal", mealId], meal);
      void invalidateLists();
    },
  });

  const reanalyzeM = useMutation({
    mutationFn: (hint: string | null) =>
      reanalyzeMeal(mealId, hint ? { hint } : {}),
    onSuccess: (meal) => {
      qc.setQueryData(["meal", mealId], meal);
      void invalidateLists();
      setReanalyzeOpen(false);
      setReanalyzeHint("");
    },
  });

  const addPhotoM = useMutation({
    mutationFn: (file: File) => addMealPhoto(mealId, file, { analyze: true }),
    onSuccess: (res) => applyResponse(res),
  });

  const deletePhotoM = useMutation({
    mutationFn: (photoId: number) => deleteMealPhoto(mealId, photoId),
    onSuccess: (res) => applyResponse(res),
  });

  const deleteM = useMutation({
    mutationFn: (itemId: number) => deleteMealItem(mealId, itemId),
    onSuccess: applyResponse,
  });

  const addM = useMutation({
    mutationFn: (payload: MealItemCreate) => addMealItem(mealId, payload),
    onSuccess: applyResponse,
  });

  if (!Number.isFinite(mealId) || mealId <= 0) {
    return (
      <div className="mx-auto max-w-7xl space-y-5">
        <BackButton to="/nutrition" label={t("meal_detail.back_to_nutrition")} withHome={false} />
        <Card>
          <p className="text-[13px] text-taupe">{t("meal_detail.invalid_id")}</p>
        </Card>
      </div>
    );
  }

  if (mealQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("meal_detail.loading")}</div>;
  }
  if (!mealQ.data) {
    return (
      <div className="mx-auto max-w-7xl space-y-5">
        <BackButton to="/nutrition" label="К питанию" withHome={false} />
        <Card>
          <p className="text-[13px] text-taupe">{t("meal_detail.not_found")}</p>
        </Card>
      </div>
    );
  }

  const meal = mealQ.data;
  const aiItemsCount = meal.items.filter((i) => i.is_ai_generated).length;
  const editedItemsCount = meal.items.filter((i) => !i.is_ai_generated).length;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <BackButton to="/nutrition" label="К питанию" withHome={false} />

      <header>
        <SectionLabel>
          {MEAL_TYPE_LABEL[meal.meal_type] ?? meal.meal_type}
          {" · "}
          {fmtDate(meal.date, "d MMMM")}
          {meal.time ? ` · ${meal.time.slice(0, 5)}` : ""}
        </SectionLabel>
        {editingDescription ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateMetaM.mutate({ description: descriptionDraft });
              setEditingDescription(false);
            }}
            className="mt-1 flex flex-col gap-2"
          >
            <textarea
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-amber/50 bg-cream-light px-3 py-2 font-serif text-[20px] leading-snug"
              autoFocus
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={updateMetaM.isPending}>
                {updateMetaM.isPending ? "…" : t("meal_detail.save")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditingDescription(false)}
              >
                {t("meal_detail.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDescriptionDraft(meal.description || "");
              setEditingDescription(true);
            }}
            className="group mt-1 inline-flex items-start gap-2 text-left font-serif text-[22px] font-medium leading-snug md:text-[26px]"
            title={t("meal_detail.edit_description")}
          >
            <span>{meal.description || t("meal_detail.fallback_meal_name")}</span>
            <Pencil
              className="mt-1.5 h-4 w-4 shrink-0 text-taupe opacity-0 transition-opacity group-hover:opacity-100"
              strokeWidth={1.8}
            />
          </button>
        )}
        {meal.source === "photo_analysis" ? (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-taupe">
            <Sparkles className="h-3 w-3 text-amber-dark" strokeWidth={1.8} />
            {t("meal_detail.ai_recognized")}
            {meal.ai_confidence != null ? (
              <span>· confidence {Math.round(meal.ai_confidence * 100)}%</span>
            ) : null}
          </div>
        ) : null}
      </header>

      {/* Multi-photo grid */}
      <PhotosSection meal={meal} />

      {/* Low-confidence banner */}
      {meal.ai_confidence != null && meal.ai_confidence < 0.7 ? (
        <LowConfidenceBanner
          confidence={meal.ai_confidence}
          onReanalyze={() => setReanalyzeOpen(true)}
        />
      ) : null}

      {/* Alternative-dish suggestions */}
      {(() => {
        const alts = (meal.ai_analysis_raw as { alternatives?: MealAlternative[] } | null)?.alternatives;
        if (!alts || alts.length === 0) return null;
        return (
          <AlternativesPanel
            alternatives={alts}
            onPick={(alt) => {
              setReanalyzeHint(t("meal_detail.alt_hint", { name: alt.name, why: alt.why }));
              setReanalyzeOpen(true);
            }}
          />
        );
      })()}

      {/* Totals — live-updating aggregate */}
      <Card>
        <SectionLabel>{t("meal_detail.totals")}</SectionLabel>
        <div className="mt-3 grid grid-cols-5 gap-2 text-center">
          <TotalBox label={t("meal_detail.kcal")} value={n(meal.calories)} highlight />
          <TotalBox label={t("meal_detail.protein")} value={`${n(meal.protein_g, 1)} ${t("meal_detail.g")}`} />
          <TotalBox label={t("meal_detail.carbs")} value={`${n(meal.carbs_g, 1)} ${t("meal_detail.g")}`} />
          <TotalBox label={t("meal_detail.fat")} value={`${n(meal.fat_g, 1)} ${t("meal_detail.g")}`} />
          <TotalBox
            label={t("meal_detail.fiber")}
            value={meal.fiber_g != null ? `${n(meal.fiber_g, 1)} ${t("meal_detail.g")}` : "—"}
          />
        </div>
        {aiItemsCount + editedItemsCount > 0 ? (
          <div className="mt-2 text-[11px] text-taupe">
            {aiItemsCount > 0 ? `${aiItemsCount} AI · ` : ""}
            {editedItemsCount > 0 ? t("meal_detail.n_corrected", { n: editedItemsCount }) : ""}
          </div>
        ) : null}
      </Card>

      {/* Items list */}
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("meal_detail.products")}</SectionLabel>
          <span className="text-[11px] text-taupe">
            {meal.items.length === 1 ? t("meal_detail.n_items_singular", { n: meal.items.length }) : t("meal_detail.n_items_plural", { n: meal.items.length })}
          </span>
        </div>

        {meal.items.length === 0 ? (
          <p className="mt-3 text-[13px] text-taupe">
            {t("meal_detail.no_items_yet")}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {meal.items.map((item) => (
              <MealItemRow
                key={item.id}
                item={item}
                onSave={(patch) => updateM.mutate({ itemId: item.id, patch })}
                onDelete={() => {
                  if (confirm(t("meal_detail.delete_item_confirm", { name: item.name }))) deleteM.mutate(item.id);
                }}
              />
            ))}
          </ul>
        )}

        <AddItemForm onAdd={(payload) => addM.mutate(payload)} />
      </Card>

      {reanalyzeOpen ? (
        <ReanalyzeModal
          initialHint={reanalyzeHint}
          running={reanalyzeM.isPending}
          onClose={() => setReanalyzeOpen(false)}
          onSubmit={(hint) => reanalyzeM.mutate(hint || null)}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// Photos grid + add/delete
// ============================================================================

function PhotosSection({ meal }: { meal: MealEntry }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const photos = meal.photos || [];

  const addPhoto = async (file: File) => {
    setUploading(true);
    try {
      const compressed = await compressImage(file, 1024, 0.85);
      const res = await addMealPhoto(meal.id, compressed, { analyze: true });
      qc.setQueryData(["meal", meal.id], res.meal);
      void qc.invalidateQueries({ queryKey: ["nutrition-meals"] });
      void invalidateNutritionQueries(qc);
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (photoId: number) => {
    if (!confirm(t("meal_detail.delete_photo_confirm"))) return;
    const res = await deleteMealPhoto(meal.id, photoId);
    qc.setQueryData(["meal", meal.id], res.meal);
    void qc.invalidateQueries({ queryKey: ["nutrition-meals"] });
  };

  if (photos.length === 0 && !meal.photo_path && !uploading) {
    return null;
  }

  return (
    <section>
      <SectionLabel>
        {t("meal_detail.photos_section")} ({photos.length}{uploading ? ` · ${t("meal_detail.uploading")}` : ""})
      </SectionLabel>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
        {photos.map((p) => (
          <PhotoTile key={p.id} mealId={meal.id} photo={p} onDelete={() => removePhoto(p.id)} />
        ))}
        {photos.length === 0 && meal.photo_path ? (
          <SecureImage
            src={`/nutrition/meals/${meal.id}/photo`}
            alt={meal.description}
            className="aspect-square w-full rounded-lg object-cover"
          />
        ) : null}
        <AddPhotoTile
          inputRef={fileRef}
          uploading={uploading}
          onFile={(file) => void addPhoto(file)}
        />
      </div>
    </section>
  );
}

function PhotoTile({
  mealId,
  photo,
  onDelete,
}: {
  mealId: number;
  photo: MealPhoto;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg border border-cream-border bg-cream-light">
      <SecureImage
        src={`/nutrition/meals/${mealId}/photos/${photo.id}/file`}
        alt={photo.caption || "meal"}
        className="h-full w-full object-cover"
      />
      {photo.caption ? (
        <div className="absolute inset-x-2 bottom-2 rounded bg-navy/80 px-2 py-1 text-[11px] text-cream-light">
          {photo.caption}
        </div>
      ) : null}
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-navy/80 text-cream-light opacity-0 transition-opacity hover:bg-navy group-hover:opacity-100"
        aria-label={t("meal_detail.delete_photo_aria")}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function AddPhotoTile({
  inputRef,
  uploading,
  onFile,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  uploading: boolean;
  onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };
  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={uploading}
      className="flex aspect-square flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-cream-border text-taupe transition-colors hover:border-amber/50 hover:bg-cream-light disabled:opacity-60"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handle}
        className="hidden"
      />
      {uploading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.8} />
          <span className="text-[11px]">{t("meal_detail.analyzing")}</span>
        </>
      ) : (
        <>
          <Camera className="h-6 w-6" strokeWidth={1.6} />
          <span className="text-[11px]">{t("meal_detail.more_photo")}</span>
        </>
      )}
    </button>
  );
}

async function compressImage(
  file: File,
  maxSide = 1024,
  quality = 0.85,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      if (width > height && width > maxSide) {
        height = Math.round((height * maxSide) / width);
        width = maxSide;
      } else if (height > maxSide) {
        width = Math.round((width * maxSide) / height);
        height = maxSide;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no ctx"));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob"))),
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => reject(new Error("img load"));
    img.src = URL.createObjectURL(file);
  });
}

// ============================================================================
// Low-confidence banner + alternatives panel + Reanalyze modal
// ============================================================================

function LowConfidenceBanner({
  confidence,
  onReanalyze,
}: {
  confidence: number;
  onReanalyze: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-lg border-l-4 border-coral/50 bg-coral/10 p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-coral-dark" strokeWidth={2} />
      <div className="flex-1">
        <div className="text-[14px] font-medium text-navy-deep">
          {t("meal_detail.low_confidence_title", { pct: Math.round(confidence * 100) })}
        </div>
        <div className="mt-1 text-[12px] text-taupe">
          {t("meal_detail.low_confidence_desc")}
        </div>
        <button
          type="button"
          onClick={onReanalyze}
          className="mt-3 inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-[12px] font-medium text-cream-light hover:bg-navy-deep"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
          Перезапустить анализ
        </button>
      </div>
    </div>
  );
}

function AlternativesPanel({
  alternatives,
  onPick,
}: {
  alternatives: MealAlternative[];
  onPick: (alt: MealAlternative) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-amber-dark" strokeWidth={1.8} />
        <SectionLabel>{t("meal_detail.alternatives_title")}</SectionLabel>
      </div>
      <ul className="mt-3 space-y-2">
        {alternatives.map((alt, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => onPick(alt)}
              className="block w-full rounded-md border border-cream-border bg-cream-light p-3 text-left transition-colors hover:border-amber/40 hover:bg-cream-border/30"
            >
              <div className="text-[13px] font-medium text-navy-deep">{alt.name}</div>
              <div className="mt-0.5 text-[12px] text-taupe">{alt.why}</div>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ReanalyzeModal({
  initialHint,
  running,
  onClose,
  onSubmit,
}: {
  initialHint: string;
  running: boolean;
  onClose: () => void;
  onSubmit: (hint: string) => void;
}) {
  const { t } = useTranslation();
  const [hint, setHint] = useState(initialHint);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(hint.trim());
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <SectionLabel>{t("meal_detail.rerun_section")}</SectionLabel>
            <h3 className="mt-1 font-serif text-[20px] font-medium">AI Vision</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-taupe hover:text-navy-deep"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>
        <p className="text-[13px] text-taupe">
          {t("meal_detail.hint_help")}
        </p>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider text-taupe">
            {t("meal_detail.hint_label")}
          </label>
          <Input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder={t("meal_detail.hint_placeholder")}
            autoFocus
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
            {t("meal_detail.cancel")}
          </Button>
          <Button type="submit" disabled={running}>
            {running ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                {t("meal_detail.analyzing_progress")}
              </span>
            ) : (
              t("meal_detail.run")
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function TotalBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-md bg-cream px-1 py-2">
      <div className="text-[10px] uppercase tracking-wider text-taupe">{label}</div>
      <div
        className={cn(
          "metric-number mt-0.5",
          highlight ? "text-[20px]" : "text-[15px]",
          "text-navy-deep",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================================================
// One item row — view mode vs edit mode
// ============================================================================

function MealItemRow({
  item,
  onSave,
  onDelete,
}: {
  item: MealItem;
  onSave: (patch: MealItemUpdate) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  if (!editing) {
    return (
      <li className="rounded-lg border border-cream-border bg-cream p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-[14px] text-navy-deep">
                {item.name}
              </span>
              {item.is_ai_generated ? (
                <span className="inline-flex items-center gap-0.5 rounded bg-amber/15 px-1 py-0.5 text-[9px] uppercase tracking-wider text-amber-dark">
                  AI
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 rounded bg-green-700/10 px-1 py-0.5 text-[9px] uppercase tracking-wider text-green-800">
                  {t("meal_detail.edited_marker")}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[12px] text-taupe">
              <span className="metric-number text-navy-deep">{n(item.portion_g, 1)}</span> {t("meal_detail.g")}
              {" · "}
              <span className="metric-number text-navy-deep">{n(item.calories)}</span> {t("meal_detail.kcal")}
            </div>
            <div className="mt-0.5 text-[11px] text-taupe">
              {t("meal_detail.p_letter")} <span className="metric-number">{n(item.protein_g, 1)}</span>
              {" · "}
              {t("meal_detail.c_letter")} <span className="metric-number">{n(item.carbs_g, 1)}</span>
              {" · "}
              {t("meal_detail.f_letter")} <span className="metric-number">{n(item.fat_g, 1)}</span>
              {item.fiber_g > 0 ? (
                <>
                  {" · "}{t("meal_detail.fiber_letter")} <span className="metric-number">{n(item.fiber_g, 1)}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              title={t("meal_detail.edit")}
              className="rounded-md border border-cream-border bg-cream-light p-1.5 text-taupe hover:text-navy-deep"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              title={t("meal_detail.delete")}
              className="rounded-md border border-cream-border bg-cream-light p-1.5 text-coral-dark hover:bg-coral/10"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <MealItemEditor
      item={item}
      onCancel={() => setEditing(false)}
      onSave={(patch) => {
        onSave(patch);
        setEditing(false);
      }}
    />
  );
}

function MealItemEditor({
  item,
  onSave,
  onCancel,
}: {
  item: MealItem;
  onSave: (patch: MealItemUpdate) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(item.name);
  const [portion, setPortion] = useState(item.portion_g);
  const [calories, setCalories] = useState(item.calories);
  const [protein, setProtein] = useState(item.protein_g);
  const [carbs, setCarbs] = useState(item.carbs_g);
  const [fat, setFat] = useState(item.fat_g);
  const [fiber, setFiber] = useState(item.fiber_g);

  const baselinePortion = item.portion_g;
  const baselineCalories = item.calories;

  const scale = useMemo(() => {
    if (
      baselinePortion > 0 &&
      portion > 0 &&
      Math.abs(portion - baselinePortion) >= 1 &&
      // Only suggest when macros still look like the AI baseline (not manually edited yet)
      calories === baselineCalories &&
      protein === item.protein_g &&
      carbs === item.carbs_g &&
      fat === item.fat_g
    ) {
      const factor = portion / baselinePortion;
      return {
        factor,
        calories: Math.round(item.calories * factor),
        protein: +(item.protein_g * factor).toFixed(1),
        carbs: +(item.carbs_g * factor).toFixed(1),
        fat: +(item.fat_g * factor).toFixed(1),
        fiber: +(item.fiber_g * factor).toFixed(1),
      };
    }
    return null;
  }, [
    baselinePortion,
    portion,
    calories,
    baselineCalories,
    protein,
    item.protein_g,
    carbs,
    item.carbs_g,
    fat,
    item.fat_g,
    item.fiber_g,
  ]);

  const applyScale = () => {
    if (!scale) return;
    setCalories(scale.calories);
    setProtein(scale.protein);
    setCarbs(scale.carbs);
    setFat(scale.fat);
    setFiber(scale.fiber);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      name: name.trim(),
      portion_g: portion,
      calories,
      protein_g: protein,
      carbs_g: carbs,
      fat_g: fat,
      fiber_g: fiber,
    });
  };

  return (
    <li className="rounded-lg border border-amber/40 bg-amber/5 p-3">
      <form onSubmit={submit} className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-cream-border bg-cream px-2.5 py-2 text-[14px] font-medium"
          maxLength={200}
          required
        />
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("meal_detail.portion_g")} value={portion} onChange={setPortion} step={1} />
          <NumField label={t("meal_detail.kcal_label")} value={calories} onChange={setCalories} step={1} />
        </div>
        {scale ? (
          <button
            type="button"
            onClick={applyScale}
            className="inline-flex items-center gap-1 rounded-md border border-amber/50 bg-amber/10 px-2 py-1 text-[11px] text-amber-dark hover:bg-amber/20"
          >
            <Scale className="h-3 w-3" strokeWidth={2} />
            {t("meal_detail.scale_proportional", { kcal: scale.calories, p: scale.protein, c: scale.carbs, f: scale.fat })}
          </button>
        ) : null}
        <div className="grid grid-cols-4 gap-2">
          <NumField label={t("meal_detail.p_g")} value={protein} onChange={setProtein} step={0.1} />
          <NumField label={t("meal_detail.c_g")} value={carbs} onChange={setCarbs} step={0.1} />
          <NumField label={t("meal_detail.f_g")} value={fat} onChange={setFat} step={0.1} />
          <NumField label={t("meal_detail.fb_g")} value={fiber} onChange={setFiber} step={0.1} />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="inline-flex items-center gap-1"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
            {t("meal_detail.cancel")}
          </Button>
          <Button type="submit" className="inline-flex items-center gap-1">
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
            {t("meal_detail.save")}
          </Button>
        </div>
      </form>
    </li>
  );
}

function NumField({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step: number;
}) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-taupe">{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        min={0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mt-1 w-full rounded-md border border-cream-border bg-cream px-2 py-1.5 text-[14px] metric-number"
      />
    </div>
  );
}

// ============================================================================
// Inline add-item form
// ============================================================================

function AddItemForm({ onAdd }: { onAdd: (p: MealItemCreate) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [portion, setPortion] = useState(100);
  const [calories, setCalories] = useState(0);
  const [protein, setProtein] = useState(0);
  const [carbs, setCarbs] = useState(0);
  const [fat, setFat] = useState(0);
  const [fiber, setFiber] = useState(0);

  const reset = () => {
    setName("");
    setPortion(100);
    setCalories(0);
    setProtein(0);
    setCarbs(0);
    setFat(0);
    setFiber(0);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || calories <= 0) return;
    onAdd({
      name: name.trim(),
      portion_g: portion,
      calories,
      protein_g: protein,
      carbs_g: carbs,
      fat_g: fat,
      fiber_g: fiber,
    });
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-cream-border bg-cream-light px-3 py-1.5 text-[12px] font-medium text-navy-deep hover:border-amber/50"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
        {t("meal_detail.add_product")}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg border border-cream-border bg-cream-light p-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("meal_detail.product_name_placeholder")}
        className="w-full rounded-md border border-cream-border bg-cream px-2.5 py-2 text-[14px]"
        maxLength={200}
        autoFocus
        required
      />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Порция (г)" value={portion} onChange={setPortion} step={1} />
        <NumField label="Ккал" value={calories} onChange={setCalories} step={1} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <NumField label="Б (г)" value={protein} onChange={setProtein} step={0.1} />
        <NumField label="У (г)" value={carbs} onChange={setCarbs} step={0.1} />
        <NumField label="Ж (г)" value={fat} onChange={setFat} step={0.1} />
        <NumField label="Кл (г)" value={fiber} onChange={setFiber} step={0.1} />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Отмена
        </Button>
        <Button type="submit">{t("meal_detail.add")}</Button>
      </div>
    </form>
  );
}
